import { bindActionCreators, Dispatch } from 'redux';
import { Clickable, Strong } from '@ahmdigital/ui';
import { connect } from 'react-redux';
import { defaultsAll, get, has, head, isNil, parseInt, upperFirst } from 'lodash/fp';
import { Fragment, ElementType as ReactElementType } from 'react';
import { getTaxYear, isTaxTimeReminderActive } from '@ahmdigital/logic/lib/utils/get-tax-dates';

import { Customer } from '../../selectors/select-customer/types';
import { CustomerState } from '../../types/state/customer';
import { DisclaimerWrapper, StyledButton, StyledDiv, StyledP } from './styles';
import { DRAWER_IDS } from '../../ahm-constants/ui';
import { ProductType } from '../../types/product';
import { RootState } from '../../reducers';
import { URLS } from '../../models/url/constants';
import constants from '../../ahm-constants';
import drawerActionCreators from '../../actions/drawer';
import hasHospitalComponent from '../../utils/has-hospital-component';
import PriceService, { Prices } from '../../models/price';
import selectCalculatedYouthDiscountPercentage, {
  YouthDiscountPercentageType,
} from '../../selectors/select-calculated-youth-discount-percentage';
import selectCartHospitalOrPackageProduct from '../../selectors/select-cart-hospital-or-package-product';
import selectCustomer from '../../selectors/select-customer';
import selectCustomerHumanReadable from '../../selectors/select-customer-human-readable';
import selectIsExcessToggleApplicable from '../../selectors/select-is-excess-toggle-applicable';
import selectPrices from '../../selectors/select-prices';
import selectRebatePercentage from '../../selectors/select-rebate-percentage';
import useIsAfterHydration from '../../hooks/use-is-after-hydration';

export type EnrichedCustomerData = CustomerState & {
  ageBracket?: string;
  rebatePercentage?: number;
  ages?: {
    client?: string;
    spouse?: string;
  };
  effectiveProduct?: string;
  highlightExcess?: boolean;
  calculatedYouthDiscountPercentage?: number;
  highlightIncome?: boolean;
  whicsYouthDiscountPercentage?: number;
};

const getAgeSentence =
  (DataWrapper: ReactElementType) =>
  // @ts-expect-error - Automatic, Please fix when editing this file
  (ages, usePossessiveDeterminer = false) => {
    const { [constants.RELATIONSHIP.CLIENT]: clientAge, [constants.RELATIONSHIP.SPOUSE]: spouseAge } = ages || {};
    return (
      <>
        based on
        {has(constants.RELATIONSHIP.CLIENT, ages) && (
          <DataWrapper>
            {' '}
            {usePossessiveDeterminer ? 'your' : 'an'} age of {clientAge}
          </DataWrapper>
        )}
        {has(constants.RELATIONSHIP.CLIENT, ages) && has(constants.RELATIONSHIP.SPOUSE, ages) && <> and</>}
        {has(constants.RELATIONSHIP.SPOUSE, ages) && (
          <DataWrapper>
            {' '}
            {usePossessiveDeterminer ? 'your' : 'a'} partner’s age of {spouseAge}
          </DataWrapper>
        )}
      </>
    );
  };

const getYouthDiscountSentence = (DataWrapper: ReactElementType) => (customer: EnrichedCustomerData) => {
  const { ages, calculatedYouthDiscountPercentage, whicsYouthDiscountPercentage } = customer;
  const youthDiscountPercentage = whicsYouthDiscountPercentage || calculatedYouthDiscountPercentage || 0;

  if (youthDiscountPercentage > 0) {
    return (
      <>
        , a Youth Discount of <DataWrapper>{youthDiscountPercentage}%</DataWrapper> (
        {getAgeSentence(DataWrapper)(ages, true)}){' '}
      </>
    );
  }
  return (
    <>
      , <DataWrapper>excludes</DataWrapper> any Youth Discount{' '}
    </>
  );
};

const getIncomeSentence =
  (DataWrapper: ReactElementType) =>
  ({ incomeTier, incomeType }: Partial<CustomerState>) => (
    <>
      <DataWrapper>{incomeType}</DataWrapper> income of <DataWrapper>{incomeTier}</DataWrapper> per year
    </>
  );

const getRebateSentence = (DataWrapper: ReactElementType) => (customer: EnrichedCustomerData) => {
  const { ageBracket, rebatePercentage, rebate, highlightIncome } = customer;
  return rebate ? (
    <>
      Price reduced by an Australian Government Rebate of <DataWrapper>{rebatePercentage}%</DataWrapper> (
      {getAgeSentence(DataWrapper)({ [constants.RELATIONSHIP.CLIENT]: ageBracket }, false)},{' '}
      {getIncomeSentence(highlightIncome ? DataWrapper : Fragment)(customer)}).{' '}
    </>
  ) : (
    'Australian Government Rebate is not applied. '
  );
};

const getPriceSentence =
  (DataWrapper: ReactElementType) =>
  ({ scale, state, frequency }: Partial<CustomerState>) => (
    <>
      Price is for <DataWrapper>{scale}</DataWrapper> cover in <DataWrapper>{state}</DataWrapper> paying{' '}
      <DataWrapper>{frequency}</DataWrapper> by direct debit.{' '}
    </>
  );

const getLHCSentence =
  (DataWrapper: ReactElementType) =>
  ({ lhc }: { lhc: number }) =>
    lhc > 0 ? (
      <>
        {' '}
        and <DataWrapper>includes</DataWrapper> Lifetime Health Cover loading of <DataWrapper>{lhc}%</DataWrapper>.
      </>
    ) : (
      <>
        {' '}
        and <DataWrapper>excludes</DataWrapper> Lifetime Health Cover loading.{' '}
      </>
    );

const getExcessPart = (DataWrapper: ReactElementType) => (customer: Partial<CustomerState>, product: ProductType) => {
  const excessAmount = get('charges.excess.amount', product);
  const customerExcess = get('hospitalExcess', customer);

  if (isNil(excessAmount)) {
    return null;
  }

  return <DataWrapper>an excess of ${customerExcess !== excessAmount ? customerExcess : excessAmount}</DataWrapper>;
};

const getProductSentence =
  (DataWrapper: ReactElementType) => (customer: EnrichedCustomerData, product: ProductType | undefined) => {
    const { highlightExcess } = customer;

    if (!product || get('type', product) === constants.PRODUCT_TYPE.EXTRAS) {
      return null;
    }

    return (
      <>
        {' '}
        {upperFirst(constants.PRODUCT_TYPE.HOSPITAL)} price includes{' '}
        {getExcessPart(highlightExcess ? DataWrapper : Fragment)(customer, product)}
        {getYouthDiscountSentence(DataWrapper)(customer)}
        {getLHCSentence(DataWrapper)(customer)}
      </>
    );
  };

const getYouthDiscountPercentageFromPrices = (prices: Prices, productId: string, customer: Partial<CustomerState>) => {
  const price = PriceService.getPrice(prices, productId, customer);
  return get('youthDiscount', price);
};

export type ElementType = 'button' | 'clickable';

type PriceDisclaimerProps = {
  bold?: boolean;
  calculatedYouthDiscountPercentage: YouthDiscountPercentageType;
  cartHospitalOrPackageProduct?: ProductType;
  center?: boolean;
  className?: string;
  constrainWidth?: boolean;
  customer: Customer;
  customerReadable: unknown;
  elementType?: ElementType;
  forProducts?: ProductType[];
  hideForDesktop?: boolean;
  highlightExcess?: boolean;
  highlightIncome?: boolean;
  indicator?: string;
  openChangeDetailsDrawer: () => void;
  prices: Prices;
  rebatePercentage: number;
  showRefineLink?: boolean;
  showRefineLinkInline?: boolean;
};

const PriceDisclaimer = ({
  bold = false,
  calculatedYouthDiscountPercentage,
  cartHospitalOrPackageProduct,
  center = false,
  className,
  constrainWidth = false,
  customer,
  customerReadable,
  elementType = 'clickable',
  forProducts = [] as ProductType[],
  hideForDesktop = false,
  highlightExcess = false,
  highlightIncome = false,
  indicator = '*',
  openChangeDetailsDrawer,
  prices,
  rebatePercentage,
  showRefineLink = false,
  showRefineLinkInline = false,
}: PriceDisclaimerProps) => {
  const DataWrapper = bold ? Strong : Fragment;
  const radix = 10;
  const isAfterHydration = useIsAfterHydration();
  const showTaxReminder = isTaxTimeReminderActive();
  const currentYear = getTaxYear();

  // We check if the product excess matches customer excess, because we display greyed out products of other excess types on the comparer.

  const hospitalProductWithExcess = forProducts.find((product) => {
    const customerExcess = parseInt(radix, get('hospitalExcess', customer));
    const hasExcessMatchingCustomer = customerExcess === get('charges.excess.amount', product);
    return hasHospitalComponent(product) && hasExcessMatchingCustomer;
  });

  const product = hospitalProductWithExcess || head(forProducts) || cartHospitalOrPackageProduct;
  const effectiveProduct = isAfterHydration ? product : undefined;
  const effectiveHighlightExcess = isAfterHydration ? highlightExcess : false;
  const effectiveHighlightIncome = isAfterHydration ? highlightIncome : false;
  const effectiveProductId = effectiveProduct?.id;
  const whicsYouthDiscountPercentage = effectiveProductId
    ? getYouthDiscountPercentageFromPrices(prices, effectiveProductId, customer)
    : undefined;

  const customerData: EnrichedCustomerData = defaultsAll([
    {
      calculatedYouthDiscountPercentage,
      highlightExcess: effectiveHighlightExcess,
      highlightIncome: effectiveHighlightIncome,
      rebatePercentage,
      whicsYouthDiscountPercentage,
    },
    customerReadable,
    customer,
  ]);

  let refinePriceElement = null;
  if (showRefineLink) {
    refinePriceElement =
      elementType === 'button' ? (
        <div>
          <StyledButton onClick={openChangeDetailsDrawer} variant={['border', 'wide', 'white']}>
            Refine price
          </StyledButton>
        </div>
      ) : (
        <StyledDiv center={center}>
          <Clickable onClick={openChangeDetailsDrawer}>
            <Strong variant="small14">Refine these details</Strong>
          </Clickable>
        </StyledDiv>
      );
  }

  const Paragraph = StyledP;

  return (
    <DisclaimerWrapper hideForDesktop={hideForDesktop} data-nosnippet>
      {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: No overload matches this call. */}
      <Paragraph $constrainWidth={constrainWidth} className={className}>
        {indicator}
        {getPriceSentence(DataWrapper)(customerData)}
        {getRebateSentence(DataWrapper)(customerData)}
        {getProductSentence(DataWrapper)(customerData, effectiveProduct)}
        {showTaxReminder && (
          <>
            Heads up, the income thresholds used to calculate the Australian Government Rebate (AGR) and Medicare Levy
            Surcharge (MLS) are changing from 1 July {currentYear}, so your entitlement to the AGR may change after that
            date.{' '}
            <Clickable to={URLS.HELP.WHAT_IS_AGR}>More on the AGR & MLS changes and how this may affect you</Clickable>
          </>
        )}
        {showRefineLinkInline && (
          <Clickable onClick={openChangeDetailsDrawer}>
            <Strong>Refine price</Strong>
          </Clickable>
        )}
      </Paragraph>
      {refinePriceElement}
    </DisclaimerWrapper>
  );
};

const mapStateToProps = (state: RootState) => ({
  calculatedYouthDiscountPercentage: selectCalculatedYouthDiscountPercentage(state),
  cartHospitalOrPackageProduct: selectCartHospitalOrPackageProduct(state) || undefined,
  customer: selectCustomer(state),
  customerReadable: selectCustomerHumanReadable(state),
  highlightExcess: selectIsExcessToggleApplicable(state, undefined),
  prices: selectPrices(state),
  rebatePercentage: selectRebatePercentage(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => {
  const drawerActions = bindActionCreators(drawerActionCreators, dispatch);

  return {
    openChangeDetailsDrawer: () => drawerActions.openDrawer(DRAWER_IDS.CHANGE_DETAILS),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PriceDisclaimer);

export {
  getAgeSentence,
  getExcessPart,
  getIncomeSentence,
  getLHCSentence,
  getProductSentence,
  getRebateSentence,
  getYouthDiscountSentence,
  mapStateToProps,
  PriceDisclaimer as PriceBreakdownForTest,
};
